"use client";

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  removeGroupedCartItem,
  updateGroupedCartItem,
} from "@/cart/groupedCartMutationHelpers";
import { privateAttributes } from "@/cart/cartAttributes";
import { getCartBundle } from "@/cart/cartBundle";
import LandingPackageCodeNotice from "@/components/Cart/LandingPackageCodeNotice";
import useCart from "@/cart/useCart";
import {
  getDisplayCartItems,
  getDisplayCartQuantity,
  getLineEffectiveTotalAmount,
  getMaticCount,
  getVariantQuantity,
  type DisplayCartItem,
} from "@/cart/displayCart";
import {
  formatPrice,
  isMatic,
  isMaticCare,
  isMaticSerene,
} from "@/utils/ShopifyHelpers";
import { PET_BRUSH_ROLL_VARIANT_ID } from "@/consts/shopify";
import { FREE_SHIPPING_THRESHOLD } from "@/cart/consts";
import { isMaticFamilyVariantId } from "@/utils/maticFamily";
import { X, Gift, ChevronLeft } from "lucide-react";
import posthog from "posthog-js";
import useLayoutStore from "@/stores/useLayoutStore";
import { cn } from "@/lib/utils";
import { useAnnounce } from "@/hooks/useAnnounce";
import { useFocusRetention } from "@/hooks/useFocusRetention";
import { useGiftForm } from "./hooks";
import SideCartGiftForm from "./SideCartGiftForm";
import SideCartItem from "./SideCartItem";
import SideCartFooter from "./SideCartFooter";
import { CartMessageToast, CartUndoToast } from "./CartToast";
import TrustGrid from "./TrustGrid";
import { useTrack } from "@/analytics/events";

const ATTRIBUTION_STAMP_TIMEOUT_MS = 1500;

export default function SideCart() {
  const isSideCartOpen = useLayoutStore((state) => state.isSideCartOpen);
  const closeSideCart = useLayoutStore((state) => state.closeSideCart);
  const sideCartTrigger = useLayoutStore((state) => state.sideCartTrigger);
  const track = useTrack();
  // Radix reports escape and outside-press separately; everything else that
  // closes the cart is the close button, so that is the default.
  const dismissMethodRef = useRef<"overlay" | "escape" | "close_button">(
    "close_button",
  );

  const {
    cart,
    addItem,
    addMatic,
    addMaticCare,
    removeItem,
    updateItem,
    addItems,
    stampAttribution,
  } = useCart();
  const router = useRouter();
  const announce = useAnnounce();
  const { capture, restore } = useFocusRetention();

  const isGift = useCart((state) => state.isGift);
  const setIsGift = useCart((state) => state.setIsGift);
  const [showGiftForm, setShowGiftForm] = useState(false);
  const giftForm = useGiftForm({ isGift, setIsGift });
  const cartHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const giftHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const previousShowGiftFormRef = useRef(showGiftForm);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollContainerNode, setScrollContainerNode] =
    useState<HTMLDivElement | null>(null);
  const setScrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    scrollContainerRef.current = node;
    setScrollContainerNode(node);
  }, []);
  const [trustGridNode, setTrustGridNode] = useState<HTMLDivElement | null>(
    null,
  );
  const setTrustGridRef = useCallback((node: HTMLDivElement | null) => {
    setTrustGridNode(node);
  }, []);
  const [mutatingItemIds, setMutatingItemIds] = useState<string[]>([]);
  const [focusRestoreRequestId, setFocusRestoreRequestId] = useState(0);
  const focusPreparedRef = useRef(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [visibleTrustGridHeight, setVisibleTrustGridHeight] = useState(0);
  const mutatingItemIdsRef = useRef<Set<string>>(new Set());
  const checkingOutRef = useRef(false);

  const liveItemsOrdered = useMemo(() => {
    const items = getDisplayCartItems(cart);

    const getSortBucket = (item: (typeof items)[number]["item"]) => {
      if (isMatic(item)) {
        return 0;
      }

      if (isMaticCare(item)) {
        return 1;
      }

      return 2;
    };

    return [...items].sort((a, b) => {
      return getSortBucket(a.item) - getSortBucket(b.item);
    });
  }, [cart]);

  const liveHasMaticInCart = useMemo(
    () => liveItemsOrdered.some((item) => isMatic(item.item)),
    [liveItemsOrdered],
  );
  // Serene ships later than Matic, so its cart says when.
  const hasSereneInCart = useMemo(
    () => liveItemsOrdered.some((item) => isMaticSerene(item.item)),
    [liveItemsOrdered],
  );
  const liveHasMaticCare = useMemo(
    () => liveItemsOrdered.some((item) => isMaticCare(item.item)),
    [liveItemsOrdered],
  );
  const liveHasPetBrushRoll =
    getVariantQuantity(cart, PET_BRUSH_ROLL_VARIANT_ID) > 0;
  const liveTotalMaticQuantity = useMemo(() => getMaticCount(cart), [cart]);
  const liveHasSingleMatic = useMemo(
    () => liveTotalMaticQuantity === 1,
    [liveTotalMaticQuantity],
  );
  const liveIsEmpty = liveItemsOrdered.length === 0;
  const liveTotalQuantity = useMemo(() => getDisplayCartQuantity(cart), [cart]);
  const liveSubtotalValue = Number(cart?.cost?.subtotalAmount?.amount ?? 0);
  const liveOriginalSubtotalValue = useMemo(
    () =>
      cart?.lines?.edges?.reduce(
        (total, { node }) =>
          total +
          Number(node?.merchandise?.price?.amount ?? 0) * (node?.quantity ?? 0),
        0,
      ) ?? 0,
    [cart],
  );
  const liveCurrencyCode =
    cart?.cost?.subtotalAmount?.currencyCode ??
    liveItemsOrdered[0]?.item.merchandise?.price?.currencyCode ??
    "USD";

  const liveShippingRemaining = Math.max(
    0,
    FREE_SHIPPING_THRESHOLD - liveSubtotalValue,
  );
  const liveHasFreeShipping = liveShippingRemaining === 0;
  const liveShippingProgress = Math.min(
    100,
    (liveSubtotalValue / FREE_SHIPPING_THRESHOLD) * 100,
  );
  const liveCartDisplay = useMemo(
    () => ({
      itemsOrdered: liveItemsOrdered,
      hasMaticInCart: liveHasMaticInCart,
      hasSingleMatic: liveHasSingleMatic,
      isEmpty: liveIsEmpty,
      totalQuantity: liveTotalQuantity,
      subtotalValue: liveSubtotalValue,
      originalSubtotalValue: liveOriginalSubtotalValue,
      currencyCode: liveCurrencyCode,
      shippingRemaining: liveShippingRemaining,
      hasFreeShipping: liveHasFreeShipping,
      shippingProgress: liveShippingProgress,
    }),
    [
      liveCurrencyCode,
      liveHasFreeShipping,
      liveHasMaticInCart,
      liveHasSingleMatic,
      liveIsEmpty,
      liveItemsOrdered,
      liveOriginalSubtotalValue,
      liveShippingProgress,
      liveShippingRemaining,
      liveSubtotalValue,
      liveTotalQuantity,
    ],
  );
  const {
    itemsOrdered,
    hasMaticInCart,
    hasSingleMatic,
    isEmpty,
    totalQuantity,
    subtotalValue,
    originalSubtotalValue,
    currencyCode,
    shippingRemaining,
    hasFreeShipping,
    shippingProgress,
  } = liveCartDisplay;

  const updateCartItem = useCallback(
    (itemId: string, newQuantity: number) => {
      return updateItem(itemId, newQuantity);
    },
    [updateItem],
  );
  const isCartMutating = mutatingItemIds.length > 0;

  const startItemMutation = useCallback((itemId: string) => {
    if (mutatingItemIdsRef.current.size > 0) {
      return false;
    }

    mutatingItemIdsRef.current.add(itemId);
    setMutatingItemIds(Array.from(mutatingItemIdsRef.current));
    return true;
  }, []);

  const finishItemMutation = useCallback((itemId: string) => {
    mutatingItemIdsRef.current.delete(itemId);
    setMutatingItemIds(Array.from(mutatingItemIdsRef.current));
  }, []);

  const scheduleFocusRestore = useCallback(() => {
    setFocusRestoreRequestId((requestId) => requestId + 1);
  }, []);

  const prepareMutationFocus = useCallback(
    (target: HTMLButtonElement) => {
      if (mutatingItemIdsRef.current.size > 0) return;

      capture(target);
      focusPreparedRef.current = true;
    },
    [capture],
  );

  const captureUnpreparedMutationFocus = useCallback(() => {
    if (!focusPreparedRef.current) {
      capture();
    }
    focusPreparedRef.current = false;
  }, [capture]);

  const getFreshCartPricingClause = useCallback((itemId?: string) => {
    const freshCart = useCart.getState().cart;
    const subtotalAmount = freshCart?.cost?.subtotalAmount;
    const amount = Number(subtotalAmount?.amount);
    const currencyCode = subtotalAmount?.currencyCode;

    let itemTotalClause = "";
    if (itemId && freshCart) {
      const displayItem = getDisplayCartItems(freshCart).find(
        (cartItem) => cartItem.id === itemId,
      );
      const itemCurrencyCode = displayItem?.item.merchandise.price.currencyCode;
      const itemTotal = displayItem?.lines.reduce(
        (total, line) => total + getLineEffectiveTotalAmount(line),
        0,
      );

      if (
        itemTotal !== undefined &&
        Number.isFinite(itemTotal) &&
        itemCurrencyCode
      ) {
        itemTotalClause = `, item total ${formatPrice(
          itemTotal,
          itemCurrencyCode,
        )}`;
      }
    }

    if (!subtotalAmount || !Number.isFinite(amount) || !currencyCode) {
      return itemTotalClause;
    }

    return `${itemTotalClause}, cart total ${formatPrice(
      amount,
      currencyCode,
    )}`;
  }, []);

  const handleAddUpsell = useCallback(
    async (
      item: DisplayCartItem,
      {
        productName,
        eventName,
        add,
      }: {
        productName: string;
        eventName: string;
        add: () => Promise<unknown>;
      },
    ) => {
      if (!startItemMutation(item.id)) {
        return;
      }
      captureUnpreparedMutationFocus();

      try {
        await add();
        posthog.capture(eventName, {
          surface: "sidecart",
        });
        announce(
          `${productName} added to cart${getFreshCartPricingClause()}`,
          "polite",
        );
      } catch (error) {
        console.error(`Failed to add ${productName}:`, error);
        toast.custom(
          (id) => (
            <CartMessageToast
              message={`Couldn't add ${productName}. Please try again.`}
              toastId={id}
            />
          ),
          { duration: 5000, toasterId: "cart" },
        );
      } finally {
        finishItemMutation(item.id);
        scheduleFocusRestore();
      }
    },
    [
      announce,
      captureUnpreparedMutationFocus,
      finishItemMutation,
      getFreshCartPricingClause,
      scheduleFocusRestore,
      startItemMutation,
    ],
  );

  const handleGoToCheckout = async (): Promise<void> => {
    if (mutatingItemIdsRef.current.size > 0 || checkingOutRef.current) {
      return;
    }
    checkingOutRef.current = true;
    posthog.capture("checkout", {
      is_gift: isGift,
      cart_id: cart?.id,
      total_quantity: cart?.totalQuantity,
      cart_value: cart?.cost?.totalAmount?.amount,
      checkout_url: cart?.checkoutUrl ?? null,
      cart_age_days: cart?.createdAt
        ? (Date.now() - new Date(cart.createdAt).getTime()) / 86_400_000
        : null,
      items: cart?.lines?.edges.map(({ node }) => ({
        product_id: node?.merchandise?.product?.id || "",
        product_name: node?.merchandise?.product?.title || "",
        variant_id: node?.merchandise?.id || "",
        variant_name: node?.merchandise?.title || "",
        price: node?.cost?.totalAmount?.amount || 0,
        quantity: node?.quantity || 0,
      })),
      source: "side_cart",
    });
    // A slow stamp only costs the order its attribution, never the checkout.
    await Promise.race([
      stampAttribution(),
      new Promise((resolve) =>
        setTimeout(resolve, ATTRIBUTION_STAMP_TIMEOUT_MS),
      ),
    ]);
    closeSideCart();
    window.location.assign(cart?.checkoutUrl || "/");
    // Released once the redirect is under way, so a page restored from the
    // back-forward cache can check out again.
    checkingOutRef.current = false;
  };

  const { resetForm } = giftForm;

  useEffect(() => {
    if (focusRestoreRequestId === 0) {
      return;
    }

    restore(cartHeadingRef);
  }, [focusRestoreRequestId, restore]);

  useEffect(() => {
    if (!hasSingleMatic) {
      if (isGift) {
        resetForm();
      }
      setShowGiftForm(false);
    }
  }, [resetForm, hasSingleMatic, isGift]);

  useEffect(() => {
    if (!isSideCartOpen || previousShowGiftFormRef.current === showGiftForm) {
      return;
    }

    previousShowGiftFormRef.current = showGiftForm;
    const frameId = window.requestAnimationFrame(() => {
      if (showGiftForm) {
        giftHeadingRef.current?.focus();
      } else {
        cartHeadingRef.current?.focus();
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isSideCartOpen, showGiftForm]);

  useEffect(() => {
    if (isSideCartOpen) {
      const trigger = sideCartTrigger ?? "nav";
      posthog.capture("cart_viewed", {
        source: "side_cart",
        trigger,
        cart_id: cart?.id,
        total_quantity: cart?.totalQuantity,
        cart_value: cart?.cost?.totalAmount?.amount,
      });
      track("cart_opened", {
        trigger,
        cart_value: cart?.cost?.totalAmount?.amount ?? null,
        item_count: cart?.totalQuantity ?? null,
      });
      dismissMethodRef.current = "close_button";
    }
  }, [isSideCartOpen]);

  const handleCloseSideCart = useCallback(() => {
    track("cart_dismissed", {
      trigger: dismissMethodRef.current,
    });
    closeSideCart();
    setShowGiftForm(false);
    toast.dismiss();
  }, [closeSideCart, track]);

  useEffect(() => {
    router.events.on("routeChangeStart", closeSideCart);
    return () => router.events.off("routeChangeStart", closeSideCart);
  }, [router.events, closeSideCart]);

  useEffect(() => {
    const scrollContainer = scrollContainerNode;
    const bottomScrollThreshold = 8;

    if (!scrollContainer) {
      setIsScrolledToBottom(false);
      setVisibleTrustGridHeight(0);
      return;
    }

    let frameId: number | null = null;

    const updateScrollMetrics = () => {
      frameId = null;

      const distanceFromBottom =
        scrollContainer.scrollHeight -
        scrollContainer.scrollTop -
        scrollContainer.clientHeight;
      const nextIsScrolledToBottom =
        distanceFromBottom <= bottomScrollThreshold;

      setIsScrolledToBottom((prev) =>
        prev === nextIsScrolledToBottom ? prev : nextIsScrolledToBottom,
      );

      if (!trustGridNode) {
        setVisibleTrustGridHeight((prev) => (prev === 0 ? prev : 0));
        return;
      }

      const trustGridRect = trustGridNode.getBoundingClientRect();
      const scrollContainerRect = scrollContainer.getBoundingClientRect();
      const visibleHeight =
        Math.min(trustGridRect.bottom, scrollContainerRect.bottom) -
        Math.max(trustGridRect.top, scrollContainerRect.top);
      const nextVisibleTrustGridHeight = Math.max(
        0,
        Math.min(visibleHeight, trustGridRect.height),
      );

      setVisibleTrustGridHeight((prev) =>
        prev === nextVisibleTrustGridHeight ? prev : nextVisibleTrustGridHeight,
      );
    };

    const scheduleScrollMetricsUpdate = () => {
      if (frameId !== null) {
        return;
      }

      frameId = window.requestAnimationFrame(updateScrollMetrics);
    };

    scheduleScrollMetricsUpdate();
    scrollContainer.addEventListener("scroll", scheduleScrollMetricsUpdate, {
      passive: true,
    });

    let resizeObserver: ResizeObserver | null = null;

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", scheduleScrollMetricsUpdate);
    } else {
      resizeObserver = new ResizeObserver(() => {
        scheduleScrollMetricsUpdate();
      });

      resizeObserver.observe(scrollContainer);
      if (trustGridNode) {
        resizeObserver.observe(trustGridNode);
      }
    }

    return () => {
      scrollContainer.removeEventListener(
        "scroll",
        scheduleScrollMetricsUpdate,
      );
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", scheduleScrollMetricsUpdate);
      }
    };
  }, [
    cart?.totalQuantity,
    hasMaticInCart,
    isEmpty,
    isSideCartOpen,
    scrollContainerNode,
    showGiftForm,
    trustGridNode,
  ]);

  const toastBottomOffset = visibleTrustGridHeight + 24;

  return (
    <Sheet
      open={isSideCartOpen}
      onOpenChange={(open: boolean) => {
        if (!open) {
          handleCloseSideCart();
        }
      }}
    >
      <SheetContent
        side="right"
        aria-label="Shopping cart"
        aria-describedby={undefined}
        className="z-[80] flex !h-auto !min-h-[100dvh] !w-full !max-w-none transform-gpu flex-col !gap-0 !p-0 md:!max-w-[523px] maxed:!max-w-[max(523px,calc((100vw-1600px)/2+100px))] [&>button:first-child]:hidden"
        overlayClassName="z-[80] !min-h-[100dvh] !bg-glass-overlay backdrop-blur-glass-overlay animate-in fade-in-0 duration-300"
        onEscapeKeyDown={() => {
          dismissMethodRef.current = "escape";
        }}
        onPointerDownOutside={() => {
          dismissMethodRef.current = "overlay";
        }}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          cartHeadingRef.current?.focus();
        }}
      >
        {/* bottom-fill: extends behind cart to cover any viewport gap on mobile browsers */}
        <div className="absolute inset-x-0 top-full z-40 h-[200px] bg-background" />
        {/* Header */}
        <div className="shrink-0 border-t border-[#DBDBDB] shadow-[inset_0_-1px_0_#DBDBDB]">
          <SheetHeader className="h-header !flex-row items-center !space-y-0 px-6">
            {showGiftForm && hasSingleMatic ? (
              <div className="relative flex w-full items-center justify-between">
                <button
                  type="button"
                  onClick={() => setShowGiftForm(false)}
                  aria-label="Back to cart"
                  className="flex w-[48px] items-center md:w-[120px]"
                >
                  <ChevronLeft
                    className="-ml-[9px] h-6 w-6 text-[#1f2022]"
                    strokeWidth={1}
                  />
                </button>
                <div className="absolute left-1/2 flex -translate-x-1/2 items-center justify-center gap-2 whitespace-nowrap">
                  <Gift
                    className="h-[22px] w-[22px] text-[#1f2022] md:h-5 md:w-5"
                    strokeWidth={1}
                  />
                  <h2
                    ref={giftHeadingRef}
                    tabIndex={-1}
                    className="font-trimMono text-[14px] uppercase tracking-[0.42px] text-[#1f2022] outline-none focus-visible:ring-2 focus-visible:ring-[#1f2022] focus-visible:ring-offset-2"
                  >
                    ADD YOUR GIFT DETAILS
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowGiftForm(false);
                    giftForm.resetForm();
                  }}
                  aria-label="Cancel gift details"
                  className="flex w-[48px] items-center justify-end md:w-[120px]"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            ) : (
              <div className="flex w-full items-center justify-between">
                <SheetTitle
                  ref={cartHeadingRef}
                  tabIndex={-1}
                  className="!font-ppMori !text-[16px] !font-normal !leading-[1.6] !tracking-[-0.32px] !text-[#54524e]"
                >
                  <span className="sr-only">Shopping </span>
                  Cart ({totalQuantity})
                </SheetTitle>
                <button
                  type="button"
                  onClick={handleCloseSideCart}
                  aria-label="Close cart"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            )}
          </SheetHeader>
        </div>

        <div className="relative flex-1 overflow-hidden">
          <div
            ref={setScrollContainerRef}
            className="absolute inset-0 overflow-y-auto"
          >
            {showGiftForm && hasSingleMatic ? (
              <SideCartGiftForm
                giftForm={giftForm}
                scrollContainerRef={scrollContainerRef}
              />
            ) : (
              <div className="flex min-h-full flex-col">
                {isEmpty && (
                  <div className="mx-6 mt-6 flex flex-col gap-2 bg-[#f5f3ef] p-4">
                    <p className="text-center font-trimMono text-[14px] uppercase tracking-[0.42px] text-[#1f2022]">
                      YOUR CART IS EMPTY
                    </p>
                  </div>
                )}

                {!isEmpty && (
                  <>
                    <div className="px-6 py-6">
                      {itemsOrdered.map((item, index) => {
                        return (
                          <Fragment key={item.id}>
                            <SideCartItem
                              item={item.item}
                              groupedItems={item.lines}
                              isLast={index === itemsOrdered.length - 1}
                              quantity={item.quantity}
                              maxQuantity={
                                isMaticCare(item.item) ||
                                item.item.merchandise.id ===
                                  PET_BRUSH_ROLL_VARIANT_ID
                                  ? Math.max(1, liveTotalMaticQuantity)
                                  : item.item.merchandise.quantityAvailable
                              }
                              onChange={async (newQty) => {
                                if (!startItemMutation(item.id)) {
                                  return;
                                }
                                captureUnpreparedMutationFocus();

                                const itemName =
                                  item.item.merchandise.product.title;
                                try {
                                  await updateGroupedCartItem({
                                    item,
                                    newQuantity: newQty,
                                    getCartState: useCart.getState,
                                    setCartState: useCart.setState,
                                    updateCartItem,
                                    removeItem,
                                  });
                                  announce(
                                    `${itemName} quantity ${newQty}${getFreshCartPricingClause(item.id)}`,
                                    "polite",
                                  );
                                } catch (error) {
                                  console.error(
                                    "Failed to update cart quantity:",
                                    error,
                                  );
                                  announce(
                                    `Couldn't update ${itemName}. Please try again.`,
                                    "assertive",
                                  );
                                } finally {
                                  finishItemMutation(item.id);
                                  scheduleFocusRestore();
                                }
                              }}
                              onAddMaticCare={
                                liveHasMaticCare
                                  ? undefined
                                  : () =>
                                      handleAddUpsell(item, {
                                        productName: "Matic Care",
                                        eventName: "matic_care_upsell",
                                        add: () =>
                                          addMaticCare(
                                            { source: "side_cart" },
                                            item.quantity,
                                          ),
                                      })
                              }
                              onAddPetBrushRoll={
                                liveHasPetBrushRoll
                                  ? undefined
                                  : () =>
                                      handleAddUpsell(item, {
                                        productName: "Pet Brush Roll",
                                        eventName: "pet_brush_roll_upsell",
                                        add: () =>
                                          addItem(
                                            PET_BRUSH_ROLL_VARIANT_ID,
                                            {
                                              source: "side_cart",
                                            },
                                            item.quantity,
                                          ),
                                      })
                              }
                              hasSingleMatic={hasSingleMatic}
                              isGift={isGift}
                              setIsGift={setIsGift}
                              isMutating={isCartMutating}
                              onInteractionStart={prepareMutationFocus}
                              onRemove={async () => {
                                if (!startItemMutation(item.id)) return;
                                captureUnpreparedMutationFocus();

                                const itemName =
                                  item.item.merchandise.product.title;
                                const variantId = item.item.merchandise.id;
                                try {
                                  await removeGroupedCartItem({
                                    item,
                                    getCartState: useCart.getState,
                                    setCartState: useCart.setState,
                                    removeItem,
                                  });
                                } catch (error) {
                                  console.error(
                                    "Failed to remove cart item:",
                                    error,
                                  );
                                  announce(
                                    `Couldn't remove ${itemName}. Please try again.`,
                                    "assertive",
                                  );
                                  return;
                                } finally {
                                  finishItemMutation(item.id);
                                  scheduleFocusRestore();
                                }
                                setTimeout(
                                  () =>
                                    toast.custom(
                                      (id) => (
                                        <CartUndoToast
                                          itemName={itemName}
                                          toastId={id}
                                          onUndo={() =>
                                            getCartBundle(item.item) !== null
                                              ? addItems(
                                                  item.lines.map((line) => ({
                                                    variantId:
                                                      line.merchandise.id,
                                                    quantity: line.quantity,
                                                    attributes:
                                                      privateAttributes(
                                                        line.attributes,
                                                      ),
                                                  })),
                                                  {
                                                    source: "side_cart",
                                                    buttonLocation:
                                                      "side-cart-undo",
                                                  },
                                                  {
                                                    cartAttributes:
                                                      getCartBundle(item.item)
                                                        ?.cartAttributes ?? {},
                                                  },
                                                )
                                              : isMaticFamilyVariantId(
                                                    variantId,
                                                  )
                                                ? addMatic(
                                                    {
                                                      source: "side_cart",
                                                      buttonLocation:
                                                        "side-cart-undo",
                                                    },
                                                    item.quantity,
                                                    { variantId },
                                                  )
                                                : addItem(
                                                    variantId,
                                                    undefined,
                                                    item.quantity,
                                                  )
                                          }
                                        />
                                      ),
                                      { duration: 3000, toasterId: "cart" },
                                    ),
                                  350,
                                );
                              }}
                            />
                          </Fragment>
                        );
                      })}
                    </div>
                    {hasMaticInCart && (
                      <div className="mt-auto">
                        <TrustGrid
                          ref={setTrustGridRef}
                          hasSerene={hasSereneInCart}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <div
            className="pointer-events-none absolute inset-x-0 transition-[bottom] duration-300 ease-in-out"
            style={{
              bottom: toastBottomOffset,
            }}
          >
            <Toaster
              id="cart"
              position="bottom-left"
              className="pointer-events-auto !w-full"
              toastOptions={{
                className: "!w-full",
              }}
              style={{
                position: "static",
                transform: "none",
                ["--width" as string]: "100%",
                ["--offset" as string]: "0px",
              }}
            />
          </div>
        </div>

        <motion.div
          initial={false}
          animate={
            isEmpty ? { height: 0, opacity: 0 } : { height: "auto", opacity: 1 }
          }
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="shrink-0 overflow-hidden bg-[#faf9f7]"
        >
          <div
            className={cn(
              "transition-[padding] duration-300 ease-in-out",
              hasMaticInCart && isScrolledToBottom ? "px-8 md:px-12" : "px-0",
            )}
          >
            <div className="h-px bg-legacy-neutral" />
          </div>
          <LandingPackageCodeNotice cart={cart} />
          <SideCartFooter
            showGiftForm={showGiftForm}
            hasSingleMatic={hasSingleMatic}
            totalQuantity={totalQuantity}
            subtotalValue={subtotalValue}
            originalSubtotalValue={originalSubtotalValue}
            currencyCode={currencyCode}
            giftForm={giftForm}
            isGift={isGift}
            handleGoToCheckout={handleGoToCheckout}
            setShowGiftForm={setShowGiftForm}
            scrollContainerRef={scrollContainerRef}
            hasFreeShipping={hasFreeShipping}
            shippingRemaining={shippingRemaining}
            shippingProgress={shippingProgress}
            disableCheckout={isCartMutating}
          />
        </motion.div>
      </SheetContent>
    </Sheet>
  );
}
